import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/ui/buttons/button";
import { Form } from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { TextArea } from "@/components/ui/textarea/textarea";
import { GeneralModal } from "@/features/modals/general-modal";

import type { ShadowFloor } from "../../types/location-map";

import { useUpdateFloor } from "../../hooks/useMapEntityUpdates";
import { floorImageDisplayUrl } from "../../map/floors/floor-map-images";
import { toShadowFloorUpdate } from "../../utils/location/to-shadow-wire";
import { toTitleCase } from "../../utils/shared/strings";

type EditFloorFormProps = {
	floor: ShadowFloor;
	onClose: () => void;
};

type EditFloorFormValues = {
	description: string;
	name: string;
	opacity: string;
};

const parseOpacity = (value: string): null | number => {
	const trimmed = value.trim();
	if (!trimmed) return null;
	const opacity = Number(trimmed);
	if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) return null;
	return opacity;
};

/**
 * Name, description, and floor-plan placement updates.
 * Corner moves on the map should call {@link toShadowFloorUpdate} with the
 * full image (key, pixel size, and the four corners).
 */
export const EditFloorForm = ({ floor, onClose }: EditFloorFormProps) => {
	const updateFloor = useUpdateFloor();
	const [submitError, setSubmitError] = useState<null | string>(null);
	const [clearImage, setClearImage] = useState(false);
	const hasImage = Boolean(floor.image) && !clearImage;
	const imageIsOnMap = Boolean(
		floor.image && floorImageDisplayUrl(floor.image, floor.imageUrl),
	);

	const {
		control,
		formState: { errors, isValid },
		handleSubmit,
		reset,
	} = useForm<EditFloorFormValues>({
		defaultValues: {
			description: floor.description ?? "",
			name: toTitleCase(floor.name),
			opacity:
				floor.image?.opacity !== undefined ? String(floor.image.opacity) : "",
		},
		mode: "onChange",
	});

	useEffect(() => {
		queueMicrotask(() => {
			setClearImage(false);
			reset({
				description: floor.description ?? "",
				name: toTitleCase(floor.name),
				opacity:
					floor.image?.opacity !== undefined ? String(floor.image.opacity) : "",
			});
		});
	}, [floor.description, floor.id, floor.image, floor.name, reset]);

	const canSubmit = isValid && !updateFloor.isPending;

	const onSubmit = handleSubmit((data) => {
		const trimmedDescription = data.description.trim();
		const image = floor.image;
		const opacity = parseOpacity(data.opacity);

		setSubmitError(null);
		updateFloor.mutate(
			{
				body: toShadowFloorUpdate({
					description:
						trimmedDescription.length > 0 ? trimmedDescription : null,
					...(clearImage
						? { image: null }
						: image
							? {
									image: {
										...image,
										...(opacity !== null ? { opacity } : {}),
									},
								}
							: {}),
					name: data.name.trim(),
				}),
				floorId: floor.id,
			},
			{
				onError: () => {
					setSubmitError("Could not update floor.");
				},
				onSuccess: () => {
					onClose();
				},
			},
		);
	});

	return (
		<GeneralModal
			closeOnSubmit={false}
			description="Used to group zones. A saved floor plan is drawn on the location map."
			isOpen
			onClose={onClose}
			onSubmit={onSubmit}
			submitDisabled={!canSubmit}
			submitText={updateFloor.isPending ? "Saving…" : "Save"}
			title="Manage Floor"
		>
			<Form
				className="flex flex-col gap-4 pb-2"
				onSubmit={onSubmit}
				validationBehavior="aria"
			>
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<Input
							errorMessage={errors.name?.message}
							isInvalid={Boolean(errors.name)}
							isRequired
							label="Floor Name"
							onBlur={field.onBlur}
							onChange={field.onChange}
							placeholder="Level 1"
							ref={field.ref}
							size="md"
							value={field.value}
						/>
					)}
					rules={{
						required: "Floor name is required",
						validate: (value) =>
							value.trim().length > 0 || "Floor name is required",
					}}
				/>

				<Controller
					control={control}
					name="description"
					render={({ field }) => (
						<TextArea
							label="Description"
							onChange={field.onChange}
							placeholder="Optional description"
							rows={3}
							value={field.value}
						/>
					)}
				/>

				{floor.image ? (
					<div className="flex flex-col gap-3 border-t border-secondary pt-4">
						<h3 className="text-sm font-semibold text-primary">Floor plan</h3>
						<p className="text-sm text-tertiary">
							{clearImage
								? "The floor plan will be removed when you save."
								: imageIsOnMap
									? "This image is drawn on the map from its saved corners."
									: "An image is saved. It appears on the map once a display URL is available."}
						</p>
						{hasImage ? (
							<Controller
								control={control}
								name="opacity"
								render={({ field }) => (
									<Input
										errorMessage={errors.opacity?.message}
										hint="0 is invisible, 1 is fully opaque. Leave blank to keep the current value."
										isInvalid={Boolean(errors.opacity)}
										label="Opacity"
										onBlur={field.onBlur}
										onChange={field.onChange}
										placeholder="1"
										ref={field.ref}
										value={field.value}
									/>
								)}
								rules={{
									validate: (value) =>
										value.trim().length === 0 ||
										parseOpacity(value) !== null ||
										"Opacity must be between 0 and 1",
								}}
							/>
						) : null}
						<Button
							color="secondary"
							onClick={() => setClearImage((current) => !current)}
							size="sm"
							type="button"
						>
							{clearImage ? "Keep floor plan" : "Remove floor plan"}
						</Button>
					</div>
				) : (
					<p className="text-sm text-tertiary">
						No floor plan yet. Saving corners from the map attaches one.
					</p>
				)}

				{submitError ? (
					<p className="text-sm text-error-primary">{submitError}</p>
				) : null}
			</Form>
		</GeneralModal>
	);
};
